// Copyright 2025, University of Maryland, All Rights Reserved
import { type FC, useEffect } from 'react'

import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui'

import type { NodeAdminViewProps } from '../../../../core/types'
import { useAdminKnowledgeBase } from '../hooks/useAdminKnowledgeBase'
import {
  KnowledgeBaseSettingsForm,
  type KnowledgeBaseSettingsFormValues,
} from './KnowledgeBaseSettingsForm'

export const KnowledgeBaseAdminView: FC<NodeAdminViewProps> = props => {
  const {
    error,
    formattedUpdated,
    updatedBy,
    saveSettings,
    settings,
    label,
    setLabel,
    description,
    setDescription,
  } = useAdminKnowledgeBase(props)

  useEffect(() => {
    props.registerSave?.(() => saveSettings())
  }, [props.registerSave, saveSettings])

  const values: KnowledgeBaseSettingsFormValues = {
    label,
    description,
    mode: settings.mode,
    itemSchemaId: settings.itemSchemaId,
    injectionTemplate: settings.injectionTemplate,
  }

  const handleChange = (next: KnowledgeBaseSettingsFormValues) => {
    if (next.label !== label) setLabel(next.label)
    if (next.description !== description) setDescription(next.description)
    if (next.mode !== settings.mode) settings.updateSetting('mode', next.mode)
    if (next.itemSchemaId !== settings.itemSchemaId) {
      settings.updateSetting('itemSchemaId', next.itemSchemaId)
    }
    if (next.injectionTemplate !== settings.injectionTemplate) {
      settings.updateSetting('injectionTemplate', next.injectionTemplate)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Knowledge Base</CardTitle>
        <CardDescription>
          Review the guidance items available to reviewers. Workspace members manage the list from the Knowledge tab.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {(formattedUpdated || updatedBy) && (
          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
            {formattedUpdated && <span>Last updated {formattedUpdated}</span>}
            {updatedBy && <span>Updated by {updatedBy}</span>}
          </div>
        )}

        <KnowledgeBaseSettingsForm values={values} onChange={handleChange} />
      </CardContent>
    </Card>
  )
}
